<template lang="pug">
ValidationObserver(ref="dialogObserver")
  AppDialog.project-dialog(
    :key="show"
    append-to-body
    :loading="showDialogLoading"
    :show="show"
    :title="isNewForm ? 'New project' : 'Edit project'"
    @close="closeDialog"
  )
    header.mb-10(v-if="projectName")
      AppAvatarInitial(
        size="small"
        :text="projectName"
      )
      span {{ projectName }}
    AppTabs(
      v-if="!showDialogLoading"
      :selected-tab="selectedTab"
      compact
    )
      AppTab(
        label="General"
        tab-id="general"
        :has-error="validateTab('general')"
      )
        ValidationObserver(ref="general")
          ProjectDialogTabGeneral
      AppTab(
        v-if="membersEnabled"
        label="Members"
        tab-id="members"
      )
        ProjectDialogTabMembers
      AppTab(
        v-if="activeView"
        label="Budget & Limits"
        tab-id="budget"
        :has-error="validateTab('budget')"
      )
        ProjectDialogTabBudgets(
          ref="budget"
          :selected-toggle="selectedToggle"
          :validation-armed="validationArmed"
          @cleared-budget="setConfirmClearBudget"
          @member-added="setAlertDesktopAppVersion"
        )
      AppTab(
        v-if="teamsVisible"
        label="Teams"
        tab-id="teams"
        :has-error="validateTab('teams')"
      )
        ValidationObserver(ref="teams")
          ProjectDialogTabTeams
    template(#footer)
      button.btn.btn-default(@click="closeDialog") Cancel
      button.btn.btn-primary(
        :disabled="submitting"
        @click="handleSubmit"
      )
        | {{ submitButtonLabel }}
</template>
<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import ProjectDialogTabGeneral from './ProjectDialogTabGeneral.vue';
import ProjectDialogTabMembers from './ProjectDialogTabMembers.vue';
import ProjectDialogTabBudgets from './ProjectDialogTabBudgets.vue';
import ProjectDialogTabTeams from './ProjectDialogTabTeams.vue';
import { handleBudget } from '~/utils/budget_utils';
import { handleFormErrors } from '~/utils/form_utils';

const UPDATE_CLIENT_WARNING =
  'Warning: Time tracking will not automatically stop when the member limit is reached unless the member tracking time is running the latest version of the Hubstaff application.';
const CLEAR_BUDGET_CONFIRMATION_MESSAGE =
  "Removing this project's budget will remove its budget history. Are you sure?";

export default {
  name: 'ProjectsDialog',
  components: {
    ProjectDialogTabGeneral,
    ProjectDialogTabMembers,
    ProjectDialogTabBudgets,
    ProjectDialogTabTeams,
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    selectedTab: {
      type: String,
      required: true,
    },
    selectedToggle: {
      type: String,
      default: '',
    },
  },
  emits: ['close', 'background-task-scheduled'],
  data: () => ({
    confirmClearBudget: false,
    alertDesktopAppVersion: false,
    validationArmed: false,
    tabErrors: {},
  }),
  computed: {
    ...mapState(['project', 'isNewForm', 'submitting', 'errors', 'budget']),
    ...mapGetters([
      'projectData',
      'projectName',
      'teamsVisible',
      'hasMultipleProjectNames',
      'submitButtonLabel',
      'membersEnabled',
      'showDialogLoading',
      'activeView',
    ]),
  },
  watch: {
    // Validate-on-click: the first Save arms validation; after that, re-validate on any
    // project change so the tabs/fields re-flag and clear as the user fixes them.
    project: {
      deep: true,
      handler() {
        if (this.validationArmed) {
          this.runTabValidations();
        }
      },
    },
  },
  methods: {
    ...mapActions([
      'createProject',
      'loadTable',
      'setSubmitting',
      'resetFields',
      'updateField',
      'updateProject',
      'resetBudget',
    ]),
    closeDialog() {
      this.$emit('close');
      this.resetFields();
      this.validationArmed = false;
      this.tabErrors = {};
    },
    // Validate-on-click: a tab is only flagged after the first Save runs runTabValidations
    // (which fills tabErrors). Every tab ref — including the budget component — exposes validate().
    validateTab(tabId) {
      return !!this.tabErrors[tabId];
    },
    // Validate every tab in one pass and record which ones failed (the dialog observer
    // doesn't aggregate the per-tab nested observers in vee-validate v4). The $nextTick
    // lets `validationArmed` propagate to the budget's member-limit `required` rules first.
    async runTabValidations() {
      await this.$nextTick();

      const tabIds = ['general', 'budget', 'teams'];
      const results = await Promise.all(tabIds.map((id) => this.$refs[id]?.validate?.() ?? true));

      this.tabErrors = Object.fromEntries(tabIds.map((id, i) => [id, !results[i]]));

      return results.every(Boolean);
    },
    async handleSubmit() {
      this.validationArmed = true;

      if (await this.runTabValidations()) {
        await this.setSubmitting(true);

        // `projectData` is a pure getter that returns a fresh clone of the store state,
        // so checkBudget/origin below can mutate the payload without touching Vuex state.
        const payload = this.projectData;

        if (this.isNewForm) {
          this.checkBudget(payload);
          this.createProject(payload).then(this.completeRequest, this.handleErrors);
        } else {
          this.showAlertForDesktopAppVersion();
          if (this.confirmClearBudget && this.project.budget && !this.project.budget.type) {
            this.submitWithClearBudgetConfirmation();
          } else {
            this.checkBudget(payload);
            payload.origin = 'update_project_modal';
            payload.analytics = this.analyticsContext();
            this.updateProject(payload).then(this.completeRequest, this.handleErrors);
          }
        }
      }
    },
    // Which page opened the dialog and on which tab, so the backend can tell the entry points apart
    analyticsContext() {
      return { source: 'project_index', opened_tab: this.selectedTab };
    },
    showAlertForDesktopAppVersion() {
      if (this.alertDesktopAppVersion) {
        alert(UPDATE_CLIENT_WARNING);
      }
    },
    submitWithClearBudgetConfirmation() {
      if (confirm(CLEAR_BUDGET_CONFIRMATION_MESSAGE)) {
        this.updateField({ path: 'budget', value: null });
        this.updateProject({ ...this.projectData, analytics: this.analyticsContext() }).then(this.completeRequest, this.handleErrors);
      } else {
        this.setSubmitting(false);
      }
    },
    completeRequest(response) {
      if (response?.success) {
        this.handleSuccess(response);
      } else {
        this.handleErrors();
      }
    },
    handleSuccess(response) {
      let message;

      this.confirmClearBudget = false;
      this.alertDesktopAppVersion = false;
      if (this.isNewForm) {
        message = this.hasMultipleProjectNames ? 'Projects created' : 'Project created';
      } else {
        message = 'Project updated';
      }
      if (response.save_members_async) {
        message = `${message}. Members are being updated. This can take a couple of minutes. You can leave this page. We'll email you when it is complete.`;

        if (!response.background_task) {
          this.$appMessage(message);
          this.closeDialog();
          this.loadTable();

          return;
        }

        this.$emit('background-task-scheduled', response.background_task);
        this.$appMessage(message);
        this.closeDialog();

        return;
      }
      this.$appMessage(message);
      this.closeDialog();
      this.loadTable();
    },
    handleErrors() {
      handleFormErrors(this.$refs.dialogObserver, this.errors, this.$appMessage);
      if (!this.project.budget) {
        this.resetBudget();
      }
    },
    setAlertDesktopAppVersion() {
      this.alertDesktopAppVersion = true;
    },
    setConfirmClearBudget() {
      this.confirmClearBudget = true;
    },
    checkBudget(projectData) {
      const budget = this.project.budget;
      let projectDataCheck;
      let projects;

      if (Array.isArray(projectData)) {
        projectDataCheck = projectData[0].budget;
        projects = projectData;
      } else {
        projectDataCheck = projectData.budget;
        projects = [projectData];
      }

      if (!handleBudget(this.budget, projectDataCheck, budget)) {
        projects.forEach((project) => {
          delete project.budget;
        });
      }
    },
  },
};
</script>
